import React from 'react';

import * as analytics from 'analytics/Analytics';
import * as benchlingUtil from 'BenchlingUtil';
import {Sequence as ComponentSequence} from 'components/Sequence';
import {
  NUCLEOTIDE_ALIGNMENT_ROW_Y_COMPONENTS,
  NUCLEOTIDE_ALIGNMENT_ROW_Z_COMPONENTS,
  SliceComponentName,
  SliceMapType,
} from 'constants/MapsConstants';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import {SliceViewOptions} from 'molbio/maps/linear/SliceView';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import Range from 'molbio/maps/selectables/Range';
import AlignmentTabState from 'molbio/tabstates/AlignmentTabState';
import notifications from 'notifications';

function makeSliceView(
  slice: Slice,
  model: AlignmentMapState,
  tabstate: AlignmentTabState,
  range: Range,
  svgSupportsForeignObject: boolean
): SliceView {
  const alignment = model.get('alignment');

  const options: SliceViewOptions = {
    slice,
    range,
    model,
    tabstate,
    selection: model.polymer().selection(),
    trim: (model.polymer() as ComponentSequence).get('trim'),
    traceData: alignment.get('trace'),
    mapType: SliceMapType.alignment,
    y: NUCLEOTIDE_ALIGNMENT_ROW_Y_COMPONENTS,
    z: NUCLEOTIDE_ALIGNMENT_ROW_Z_COMPONENTS,
    selectionOptions: {tabstate, alignment: true},
    svgSupportsForeignObject,
    voteData: alignment.get('votes'),
  };

  if (!alignment.get('trace')) {
    options.y = options.y.filter((component) => component !== SliceComponentName.trace);
    options.z = options.z.filter((component) => component !== SliceComponentName.trace);
    options.traceData = undefined;
  }

  if (!alignment.get('votes')) {
    options.y = options.y.filter(
      (component) => ![SliceComponentName.alignmentComments, SliceComponentName.votes].includes(component)
    );
    options.z = options.z.filter(
      (component) => ![SliceComponentName.alignmentComments, SliceComponentName.votes].includes(component)
    );
    options.voteData = undefined;
  }

  return new SliceView(options);
}

function notifySuccess({id, name}: {id: string; name: string}): void {
  const formattedName = name.length > 32 ? `${name.slice(0, 29)}...` : name;
  notifications.success(
    <>
      DNA / RNA alignment <strong>{formattedName}</strong> saved.
    </>,
    {id}
  );
  analytics.track('alignment', 'saved_alignment', {name});
}

function notifyError(
  res?: {__suppress_notification?: boolean},
  ajaxOptions?: {__suppress_notification?: boolean}
): void {
  if (benchlingUtil.shouldSuppressNotification(res, ajaxOptions)) {
    return;
  }
  notifications.error('Error saving DNA / RNA alignment.');
}

export {makeSliceView, notifySuccess, notifyError};
